<template>
  <div id="loading-modal">
    <!-- persistent: fechar no clique de fora zerava o "carregando" com a simulação ainda rodando -->
    <v-dialog :value="loading_simulation" persistent :max-width="showLive ? 640 : 420">
      <v-card>
        <v-card-title class="text-h6">Running the simulation…</v-card-title>
        <v-card-text>
          <p class="mb-2">{{ message }}</p>
          <v-progress-linear
            :indeterminate="progress === null"
            :value="progress || 0"
            color="primary"
            class="mb-3"
          />
          <template v-if="showLive">
            <v-img v-if="frameUrl" :src="frameUrl" contain class="live-frame" />
            <div v-else class="text-caption">Waiting for the first frame…</div>
            <p class="text-caption mt-2 mb-0">
              Live preview of the field ({{ plotAxis }}) over the structure, updated every few seconds.
            </p>
          </template>
          <p v-else class="text-caption mb-0">
            {{ is3d
              ? "Gmsh builds the mesh and NGSolve solves the field. Usually under 2 minutes with the Accurate preset."
              : "Spectra need two runs (reference and device). Enable “Live preview” in the run options to watch the field." }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapGetters } from "vuex";

// Mesma regra do módulo 2D (_safe_path_segment) para montar a pasta da simulação
const safeSegment = (value, fallback) =>
  String(value || fallback).replace(/[^A-Za-z0-9_.-]/g, "_").replace(/^\.+|\.+$/g, "") || fallback;

const STAGES = {
  Starting: "Preparing the simulation",
  "Reference run (spectra)": "Spectra: reference run (for normalization)",
  "Device run (spectra)": "Spectra: run with your structure (and resonances)",
  "Field run": "Field picture and video",
  Finished: "Saving the results"
};

export default {
  name: "LoadingModal",
  data: () => ({
    status: null,
    frameUrl: "",
    timer: null
  }),
  computed: {
    ...mapGetters("simulator", ["loading_simulation", "plotAxis", "realtime"]),
    is3d() {
      return this.$store.state.simulator.is3d;
    },
    showLive() {
      return !this.is3d && !!this.realtime;
    },
    message() {
      if (!this.status) return this.is3d ? "Meshing and solving in 3D." : "Sending the simulation to Meep.";
      const stage = STAGES[this.status.stage] || this.status.stage;
      if (this.status.time != null && this.status.until) {
        return `${stage} — t = ${this.status.time.toFixed(1)} of ${this.status.until} µm/c`;
      }
      if (this.status.time != null) return `${stage} — t = ${this.status.time.toFixed(1)} µm/c`;
      return stage;
    },
    // Só a rodada do campo tem duração conhecida; nas de espectro o fim depende do decaimento
    progress() {
      const status = this.status;
      if (!status || !status.until || status.time == null) return null;
      return Math.min(100, (100 * status.time) / status.until);
    },
    liveFolder() {
      const state = this.$store.state.simulator;
      return `${process.env.VUE_APP_SIMULATOR_LOCATION}/storage/users/` +
        `${safeSegment(state.author, "anonymous")}/${safeSegment(state.id, "fdtd")}/`;
    }
  },
  watch: {
    loading_simulation: {
      immediate: true,
      handler(loading) {
        this.stop();
        if (loading && this.showLive) {
          this.timer = setInterval(this.poll, 1500);
        }
      }
    }
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
      this.status = null;
      this.frameUrl = "";
    },
    async poll() {
      try {
        const response = await fetch(`${this.liveFolder}live.json?t=${Date.now()}`, { cache: "no-store" });
        if (!response.ok) return;
        const status = await response.json();
        // Arquivos de uma execução anterior: ignora
        if (status.liveId !== this.$store.state.simulator.liveId) return;
        this.status = status;
        if (status.frame) this.frameUrl = `${this.liveFolder}live.png?t=${Date.now()}`;
      } catch (err) {
        // A prévia é opcional: falhas de rede não interrompem a simulação
      }
    }
  }
};
</script>
<style scoped>
.live-frame {
  background: #fff;
  border-radius: 4px;
  max-height: 50vh;
}
</style>
